import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Download, Calendar, Tag, ArrowRight, Globe, BarChart3, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design system: ONLY Red + Blue shades
const reportTypeConfig = {
  "Annual Report": { color: "bg-[#991B1B]/10 text-[#991B1B] border-[#991B1B]/20", icon: Globe },
  "Quarterly Briefing": { color: "bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20", icon: BarChart3 },
  "Monthly Signals": { color: "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20", icon: FileText },
  "Special Publication": { color: "bg-[#60A5FA]/10 text-[#60A5FA] border-[#60A5FA]/20", icon: FileText }
};

export default function ResearchReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/observatory/reports`);
      setReports(response.data.reports);
    } catch (err) {
      console.error("Reports fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const annualReports = reports.filter(r => r.report_type === "Annual Report");
  const quarterlyReports = reports.filter(r => r.report_type === "Quarterly Briefing");
  const monthlyReports = reports.filter(r => r.report_type === "Monthly Signals");

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="research-reports-page">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/research" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-[#991B1B] mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Research
            </Link>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
                <FileText className="w-4 h-4" />
                Publications
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
                Research & Reports
              </h1>
              <p className="text-lg text-[#1E3A8A] leading-relaxed">
                Access our published research, annual workforce intelligence reports, 
                quarterly briefings, and monthly signals.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reports Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
            </div>
          ) : (
            <>
              {/* Annual Reports */}
              {annualReports.length > 0 && (
                <motion.section
                  className="mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-3 h-3 rounded-full bg-[#991B1B]"></span>
                    <h2 className="font-heading text-2xl font-semibold" style={{ color: '#991B1B' }}>
                      Annual Global Workforce Intelligence Report
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {annualReports.map((report) => (
                      <ReportCard key={report.id} report={report} featured />
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Quarterly Briefings */}
              {quarterlyReports.length > 0 && (
                <motion.section
                  className="mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-3 h-3 rounded-full bg-[#1E3A8A]"></span>
                    <h2 className="font-heading text-2xl font-semibold" style={{ color: '#991B1B' }}>
                      Quarterly Workforce Intelligence Briefing
                    </h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {quarterlyReports.map((report) => (
                      <ReportCard key={report.id} report={report} />
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Monthly Signals */}
              {monthlyReports.length > 0 && (
                <motion.section
                  className="mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-3 h-3 rounded-full bg-[#3B82F6]"></span>
                    <h2 className="font-heading text-2xl font-semibold" style={{ color: '#991B1B' }}>
                      Monthly Workforce Intelligence Signals
                    </h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {monthlyReports.map((report) => (
                      <ReportCard key={report.id} report={report} compact />
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Empty State */}
              {reports.length === 0 && (
                <Card className="border-[#E5E7EB] bg-white">
                  <CardContent className="p-12 text-center">
                    <FileText className="w-16 h-16 text-[#E5E7EB] mx-auto mb-6" />
                    <h2 className="font-heading text-2xl font-semibold text-[#111827] mb-3">
                      Reports Coming Soon
                    </h2>
                    <p className="text-[#1E3A8A]">
                      Research reports will be published here as data accumulates.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function ReportCard({ report, compact = false, featured = false }) {
  const config = reportTypeConfig[report.report_type] || reportTypeConfig["Special Publication"];
  const IconComponent = config.icon;
  
  return (
    <Card 
      className={`border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow duration-300 ${featured ? 'border-l-4 border-l-[#991B1B]' : ''}`}
    >
      <CardContent className={compact ? "p-5" : "p-6"}>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-shrink-0">
            <div className={`w-12 h-12 rounded-lg ${config.color.split(' ')[0]} flex items-center justify-center`}>
              <IconComponent className={`w-6 h-6 ${config.color.split(' ')[1]}`} />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge variant="outline" className={config.color}>
                <Tag className="w-3 h-3 mr-1" />
                {report.report_type}
              </Badge>
              <span className="text-xs text-[#1E3A8A] flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(report.published_date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
            
            <h3 className="font-heading text-lg font-semibold text-[#111827] mb-2">
              {report.title}
            </h3>
            
            <p className={`text-[#1E3A8A] text-sm ${compact ? 'line-clamp-2' : ''}`}>
              {compact ? report.description : report.summary}
            </p>
          </div>
          
          <div className="flex-shrink-0 self-center">
            {report.download_url ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 border-[#E5E7EB]"
                onClick={() => window.open(report.download_url, '_blank')}
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-2 text-[#1E3A8A] border-[#E5E7EB]"
                disabled
              >
                Coming Soon
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
